/**
 * The product's name, in one place.
 *
 * It is "Get Heard" because of what the app is FOR - getting a song heard (the first section of
 * CLAUDE.md). The name had to pass the same test every feature does, which is why it says *heard*
 * and not *managed*, and the domain the owner registered completes the sentence: get heard now.
 *
 * Written as TWO WORDS everywhere a person reads it. It is an imperative sentence, not a compound
 * noun, and "GetHeard" reads like a SaaS logo of a startup that sells dashboards. The single token
 * (`getheard`) is only for the places that cannot hold a space: a domain, a handle, a site name.
 *
 * Deliberately NOT localized and never routed through t(). A product name is a proper noun, and a
 * Danish UI that says "Bliv Hørt" is talking about a different product. The tagline BESIDE it is
 * ordinary chrome copy and is localized like the rest.
 *
 * ⚠ **The last internal `musicpromoter` names went on 2026-08-04, and the reason is worth keeping
 * because this paragraph used to argue the opposite.** It said the package name, the default DB
 * filename, the systemd units and the test tmpdir prefixes were all staying: internal, no stranger
 * reads them, and the DB path load-bearing because *every existing selfhost install has its database
 * at that name.* Round 134 took the package name (publishing turned an internal name into one a
 * stranger TYPES), and the rest went when the DB argument was finally re-derived rather than
 * inherited: the package has never actually been published, so "every existing install" was the
 * owner's own, and `db.ts`'s `adoptLegacyDatabase()` carries even that one across.
 *
 * **The general form, which is the durable half: a name's justification can expire without the name
 * changing.** Both halves of this one did - the surface stopped being internal, and then the
 * population it protected turned out to be empty. Re-derive, do not inherit.
 */

/** The wordmark. Two words, title case, in every string a person reads. */
export const BRAND_NAME = "Get Heard";

/** The one-token form, for names that cannot hold a space (domains, Netlify site names, handles). */
export const BRAND_TOKEN = "getheard";

/**
 * The wordmark SPLIT for the weight-and-gradient contrast every surface draws it with - "Get" in the
 * body weight, "Heard" bolded, and a gradient running across the pair into the accent.
 *
 * ⚠ **It lives here because three surfaces were computing it and the third one got it wrong** (login,
 * 2026-08-09 owner report: *"lacking the text styling in the app logo on 'Heard' with gradient
 * styling"*). `public/index.html` hardcodes the split in markup, `landingPage.ts` sliced it off
 * `BRAND_NAME` with `lastIndexOf(" ")`, and the sign-in screen rendered the whole name as one string -
 * so it had no `<strong>` to bold and nothing for the gradient to land on. **A derivation repeated per
 * surface is a fork whether or not any copy is wrong**, which is `workspaceTimeZone`'s finding in a
 * much smaller lane.
 *
 * ⚠ **The split is on the LAST space, not the first**, so a future name of three words keeps its final
 * word as the accented one rather than accenting two. ⚠ And the rendered text must still equal
 * `BRAND_NAME` exactly - `tests/brand.test.ts` strips the tags and asserts that, because a stray or
 * lost space inside a split wordmark is a misspelt product name nothing else would catch.
 */
export const BRAND_FIRST = BRAND_NAME.slice(0, BRAND_NAME.lastIndexOf(" "));
export const BRAND_LAST = BRAND_NAME.slice(BRAND_NAME.lastIndexOf(" ") + 1);

/**
 * Where a SELFHOST owner reports a bug (§T, round 210).
 *
 * ⚠ **It lives here rather than in the frontend because it is a fact about the product**, and it is a
 * link rather than a form for a reason the §T entry states plainly: a single-owner install has nobody
 * to file a ticket TO - the operator IS the owner - and a "contact support" button there would be the
 * selfhost app phoning home, which this app has never done. The repo has issue templates and a
 * `CONTRIBUTING.md`; this is the address of them.
 */
export const ISSUES_URL = "https://github.com/wessberg/getheard/issues";
